import { SpanControl } from "@/Common/Components/SpanControl";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";

import AlertLevel1 from "@/Globals/_images/icons/vigilance/alertLevel1.svg";
import AlertLevel2 from "@/Globals/_images/icons/vigilance/alertLevel2.svg";
import AlertLevel3 from "@/Globals/_images/icons/vigilance/alertLevel3.svg";

enum VigilanceSanteAlertLevel {
    NoAlert,
    AlertLevel1, // Avoid involved medication. The risk of serious problems outweighs any potential benefit.
    AlertLevel2, // If possible, avoid involved medication. If not, adjust therapy to prevent potential problems.
    AlertLevel3 // Evaluate specific risk based on the clinical situation. Adjust therapy or monitor as needed.
}

interface IVigilanceSanteAlertProps {
    alertLevel: VigilanceSanteAlertLevel;
    alertCount: number;
    iconSizePx?: number;
    onClick?: () => void
}

class VigilanceSanteAlert extends React.Component<IVigilanceSanteAlertProps, never> {
    private readonly defaultIconSize = 24;

    public render(): JSX.Element {
        return (
            <div
                title={this.tooltip}
                onClick={this.props.onClick ?
                    () => this.props.onClick() :
                    () => this.openVigilanceAnalysis()
                }
                style={{ display: "flex", cursor: "pointer" }}
            >
                {this.props.alertLevel > 0 && (
                    <div
                        style={{
                            width: `${this.defaultIconSize}px`,
                            height: `${this.defaultIconSize}px`
                        }}
                    >
                        {this.getIconUrl()}
                    </div>
                )}
                {this.alertTextElement}
            </div>
        );
    }

    private getIconUrl(): JSX.Element {
        switch (this.props.alertLevel) {
            case VigilanceSanteAlertLevel.AlertLevel1:
                return <AlertLevel1 />;
            case VigilanceSanteAlertLevel.AlertLevel2:
                return <AlertLevel2 />;
            case VigilanceSanteAlertLevel.AlertLevel3:
                return <AlertLevel3 />;
            default:
                return null;
        }
    }

    private get alertTextElement(): JSX.Element {
        const alertText = `${this.props.alertCount} ${cacheStore.GetString("AlertLabel")}(s)`;
        const style: React.CSSProperties = { marginLeft: "10px" };

        switch (this.props.alertLevel) {
            case VigilanceSanteAlertLevel.AlertLevel1:
                return <SpanControl IsBold IsRed text={alertText} style={style} />;

            case VigilanceSanteAlertLevel.AlertLevel2:
                return <SpanControl IsBold IsOrange text={alertText} style={style} />;

            case VigilanceSanteAlertLevel.AlertLevel3:
                return <SpanControl IsBold IsDarkYellow text={alertText} style={style} />;

            default:
                return <SpanControl IsBold IsGreen text={alertText} style={style} />;
        }
    }

    private get tooltip(): string {
        switch (this.props.alertLevel) {
            case VigilanceSanteAlertLevel.AlertLevel1:
                return cacheStore.GetString("VigilanceSante_AlertLevel1Warning");

            case VigilanceSanteAlertLevel.AlertLevel2:
                return cacheStore.GetString("VigilanceSante_AlertLevel2Warning");

            case VigilanceSanteAlertLevel.AlertLevel3:
                return cacheStore.GetString("VigilanceSante_AlertLevel3Warning");

            default:
                return null;
        }
    }

    private openVigilanceAnalysis(): void {
        if (settingsStore.configuration.get("PharmacoTherapeuticActive"))
            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.GetVigilanceAnalysis);
    }
}

export { VigilanceSanteAlert };
